import React, {Component} from 'react';
import strings from '../../../utils/strings';

const normalize = (s = '') => s.replace(/[.,]/g, '').trim();
const toLower = (s = '') => normalize(s).toLowerCase();

// ---- helpers (file-scope) ----

// ---- child-screening subtest 2 helpers ----
const tokenize = (s = '') => normalize(s).split(/\s+/).filter(Boolean);

function buildRemainingCounts(words = []) {
	const m = Object.create(null);
	for (const w of words) {
		const k = w.toLowerCase();
		m[k] = (m[k] || 0) + 1;
	}
	return m;
}

function scoreAndClassifyUserTokens(userTokens, correctTokens) {
	const remaining = buildRemainingCounts(correctTokens);
	let matched = 0;

	const states = userTokens.map((t) => {
		const k = t.toLowerCase();
		if (remaining[k] > 0) {
			remaining[k] -= 1;
			matched += 1;
			return 'correct';
		}
		return 'wrong';
	});

	return {matched, states};
}

/**
 * If there are multiple correctValues, pick the one that gives the user
 * the highest matched-word count for highlighting.
 */
function pickBestCorrectTokens(userTokens, correctValues = []) {
	let best = {matched: -1, correctTokens: []};

	for (const cv of correctValues) {
		const cTokens = tokenize(cv);
		const res = scoreAndClassifyUserTokens(userTokens, cTokens);
		if (res.matched > best.matched) {
			best = {matched: res.matched, correctTokens: cTokens};
		}
	}

	return best.correctTokens;
}

function classifyAnswer(rawValue = '', correctValues = []) {
	const ans = normalize(rawValue);
	if (!ans) return {state: 'empty', text: ''};

	const exactHit = correctValues.some((v) => normalize(v) === ans);
	if (exactHit) return {state: 'correct', text: ans};

	const caseOnly = correctValues.some(
		(v) => toLower(v) === ans.toLowerCase()
	);
	if (caseOnly) return {state: 'case', text: rawValue}; // preserve user casing

	return {state: 'wrong', text: ans};
}

const CHILD_LOWERCASE_EXCEPTIONS = new Set(['zehn', 'braun']);

const formatChildSubtest3Word = (s = '') => {
	const t = normalize(s);
	if (!t) return '';

	const low = t.toLowerCase();
	if (CHILD_LOWERCASE_EXCEPTIONS.has(low)) return low;

	return low.charAt(0).toUpperCase() + low.slice(1);
};

const clsFor = (state) =>
	state === 'correct'
		? 'sub-test__answer--correct'
		: state === 'case'
			? 'sub-test__answer--case'
			: 'sub-test__answer--wrong';

class SubtestAnswers extends Component {
	constructor(props) {
		super(props);

		this.state = {
			viewMode: 'SHOW_ANSWERED',
		};
	}

	setViewMode(value) {
		this.setState({
			viewMode: value,
		});
	}

	toggleShowUnanswered() {
		if (this.state.viewMode === 'SHOW_ALL') {
			this.setViewMode('SHOW_ANSWERED');
		} else {
			this.setViewMode('SHOW_ALL');
		}
	}
	renderRepeatAnswer(repeatAnswer, key) {
		return (
			<span className="sub-test__answer--wrong" key={key}>
				<strike>{repeatAnswer + ' '}</strike>
			</span>
		);
	}

	checkWordWithArray(word, array, key) {
		const answer = word
			.replace(/\,/g, '') //eslint-disable-line
			.replace(/\./g, '') //eslint-disable-line
			.trim()
			.toLowerCase();
		for (let i = 0; i < array.length; i++) {
			const correct = array[i].trim().toLowerCase();
			if (answer === correct) {
				return (
					<span className="sub-test__answer--correct" key={key}>
						{answer + ' '}
					</span>
				);
			}
		}
		return (
			<span className="sub-test__answer--wrong" key={key}>
				{answer + ' '}
			</span>
		);
	}

	render() {
		const {subtest, testType, grade} = this.props;
		const {viewMode} = this.state;
		const {formattedAnswers, answersCorrect} = subtest;
		let notAnswered = 0;
		let answersClass = '';
		let isVisible = true;
		switch (viewMode) {
			case 'SHOW_ALL':
				answersClass = 'sub-test__answers--show-not-answered';
				break;
			case 'SHOW_ANSWERED':
				answersClass = '';
				break;
			case 'HIDE_ALL':
			default: {
				isVisible = false;
			}
		}

		switch (subtest.id) {
			case 1:
			case 3:
			case 6: {
				answersClass += ' col-4';
				break;
			}
			case 4: {
				answersClass += ' col-1';
				break;
			}
			case 5: {
				answersClass += ' col-2';
				break;
			}
			default:
				break;
		}

		const getCorrectClass = (isCorrect) => {
			return `sub-test__answer--${isCorrect ? 'correct' : 'wrong'}`;
		};
		const renderSubTest1Answers = (answer, index) => {
			const {state, text} = classifyAnswer(
				answer.value,
				answer.correctValues
			);
			const cls = clsFor(state);

			return (
				<div className="sub-test__answer" key={index}>
					<p>
						{answer.id + '. '}
						<span className={cls}>{text}</span>
					</p>
					{answer.correctValues.map((cv) => (
						<p key={cv}>
							{strings.formatString(strings.rightanswer, `${cv}`)}
						</p>
					))}
				</div>
			);
		};
		const renderSubTest2Answers = (answer, index) => {
			let wrapperClass = 'sub-test__answer';

			// "not answered" rule for this task: they didn't add any spaces
			if (!answer.value || answer.value.indexOf(' ') === -1) {
				notAnswered++;
				wrapperClass += ' sub-test__answer--not-answered';
				return (
					<div className={wrapperClass} key={index}>
						<p>
							{answer.id + '. '}
							<span className="sub-test__answer--wrong">
								{strings.notanswered}
							</span>
						</p>

						{answer.correctValues.map((correctValue) => (
							<p key={correctValue}>
								{strings.formatString(
									strings.rightanswer,
									`${correctValue}`
								)}
							</p>
						))}
					</div>
				);
			}

			// ✅ Special display ONLY for child-screening subtest 2
			if (testType === 'child-screening') {
				const userTokens = tokenize(answer.value);

				// Pick best correct chain (usually there’s only one)
				const bestCorrectTokens = pickBestCorrectTokens(
					userTokens,
					answer.correctValues
				);

				const {matched, states} = scoreAndClassifyUserTokens(
					userTokens,
					bestCorrectTokens
				);
				return (
					<div className={wrapperClass} key={index}>
						<p>
							{answer.id + '. '}
							<span>
								{userTokens.map((t, i) => (
									<span
										key={`${answer.id}-${i}-${t}`}
										className={
											states[i] === 'correct'
												? 'sub-test__answer--correct'
												: 'sub-test__answer--wrong'
										}
									>
										{t.toUpperCase() + ' '}
									</span>
								))}
							</span>
							<span className="sub-test__answer__points">{`(${matched}/4)`}</span>
						</p>

						{answer.correctValues.map((correctValue) => (
							<p key={correctValue}>
								{strings.formatString(
									strings.rightanswer,
									`${correctValue}`
								)}
							</p>
						))}
					</div>
				);
			}

			// Existing behavior for all other test types
			const correctClass = getCorrectClass(answer.isCorrect);
			return (
				<div className={wrapperClass} key={index}>
					<p>
						{answer.id + '. '}
						<span className={correctClass}>{answer.value}</span>
					</p>
					{answer.correctValues.map((correctValue) => (
						<p key={correctValue}>
							{strings.formatString(
								strings.rightanswer,
								`${correctValue}`
							)}
						</p>
					))}
				</div>
			);
		};

		const renderSubTest3Answers = (answer, index) => {
			let wrapperClass = 'sub-test__answer';
			const correctClass = getCorrectClass(answer.isCorrect);
			let answerText = answer.value;

			if (!answer.value) {
				notAnswered++;
				answerText = strings.notanswered;
				wrapperClass += ' sub-test__answer--not-answered';
			} else if (testType === 'child-screening') {
				// ✅ display rule for child-screening subtest 3
				answerText = formatChildSubtest3Word(answerText);
			}

			return (
				<div className={wrapperClass} key={index}>
					<p>
						{answer.id + '. '}
						<span className={correctClass}>{answerText}</span>
					</p>

					{answer.correctValues.map((correctValue) => {
						const displayCorrect =
							testType === 'child-screening'
								? formatChildSubtest3Word(correctValue)
								: `${correctValue}`;

						return (
							<p key={correctValue}>
								{strings.formatString(
									strings.rightanswer,
									displayCorrect
								)}
							</p>
						);
					})}
				</div>
			);
		};

		const renderSubTest4Answers = (answers, index) => {
			let isEvaluated = [];
			let spanArray = [];
			let medFlag = 2;
			let newAnswers = answersCorrect;

			if (testType === 'child-screening') {
				newAnswers = answersCorrect[String(grade)].value;
			}

			{
				answers.map((subanswer, index) => {
					if (subanswer.value.toLowerCase() === 'med') {
						medFlag = medFlag - 1;
					}
					if (
						!isEvaluated.includes(
							subanswer.value
								.replace(/\,/g, '')
								.replace(/\./g, '')
								.trim()
								.toLowerCase()
						)
					) {
						if (subanswer.value.toLowerCase() !== 'med')
							isEvaluated.push(subanswer.value.toLowerCase());
						if (
							subanswer.value.toLowerCase() === 'med' &&
							medFlag <= 0
						)
							isEvaluated.push(subanswer.value.toLowerCase());

						spanArray.push(
							this.checkWordWithArray(
								subanswer.value,
								newAnswers,
								index
							)
						);
					} else {
						spanArray.push(
							this.renderRepeatAnswer(subanswer.value, index)
						);
					}
				});
			}
			return (
				<li className="sub-test__answer__li" key={`${index}`}>
					{spanArray.map((spans) => {
						return spans;
					})}
				</li>
			);
		};
		const renderSubTest5Answers = (answer, index) => {
			let wrapperClass = 'sub-test__answer';
			const correctClass = getCorrectClass(answer.isCorrect);
			let answerText = '';
			if (answer.choices && answer.value) {
				const picked =
					answer?.choices?.__byIdentity?.[answer.value] ||
					answer?.choices?.[answer.value];

				answerText = picked ? picked.choice : '';
			}

			if (!answer.value) {
				notAnswered++;
				answerText = strings.notanswered;
				wrapperClass += ' sub-test__answer--not-answered';
			}

			return (
				<div className={wrapperClass} key={index}>
					<p>
						{`${answer.id}. `}
						{answer.question ? answer.question : ''}
					</p>
					<p className={correctClass}>{answerText}</p>
				</div>
			);
		};
		const renderSubTest6Answers = (answer, index) => {
			let wrapperClass = 'sub-test__answer';
			const correctClass = getCorrectClass(answer.isCorrect);

			let answerText = '';

			if (!answer.value) {
				notAnswered++;
				answerText = strings.notanswered;
				wrapperClass += ' sub-test__answer--not-answered';
			}

			if (answer.choices && answer.value) {
				answerText = answer.choices[answer.value].choice;
			}
			wrapperClass += ' sub-test__answer--compact ';

			return (
				<div className={wrapperClass} key={index}>
					<p>
						{`${answer.id}. `}
						{answer.question ? answer.question : ''}
					</p>
					<p>
						<span className={correctClass}>{answerText}</span>
					</p>
				</div>
			);
		};

		const answers = formattedAnswers.map((answer, index) => {
			switch (subtest.id) {
				case 1: {
					return renderSubTest1Answers(answer, index);
				}
				case 2: {
					return renderSubTest2Answers(answer, index);
				}
				case 3: {
					return renderSubTest3Answers(answer, index);
				}
				case 4: {
					return renderSubTest4Answers(answer, index);
				}
				case 5: {
					return renderSubTest5Answers(answer, index);
				}
				case 6: {
					return renderSubTest6Answers(answer, index);
				}
				default:
					return '';
			}
		});

		return (
			isVisible &&
			formattedAnswers.length && (
				<div>
					<h4 className="sub-test__answers__heading">
						{strings.candidate_answers}
					</h4>
					<div className={`sub-test__answers ${answersClass}`}>
						{subtest.id === 4 && (
							<ol className="sub-test__answer__ol">{answers}</ol>
						)}
						{subtest.id !== 4 && answers}
					</div>
					{notAnswered > 0 && (
						<p className="sub-test__not-answered">
							<b>
								{strings.formatString(
									strings.testnotanswered,
									`${notAnswered}`
								)}
							</b>
							{/* <b>{`${notAnswered} oppgaver ikke besvart`}</b>{' '} */}
							<button
								className="sub-test__link lit-link-button print-hide"
								onClick={(e) => {
									e.preventDefault();
									this.toggleShowUnanswered(subtest.id);
								}}
							>
								{viewMode === 'SHOW_ALL'
									? strings.hideunanswered
									: strings.showunanswered}
							</button>
						</p>
					)}
					{/* {subtest.id === 1 && (
						<ol className="sub-test__answer__ol">
							{
								'Bitte beachten Sie, dass eine inkorrekte Groß- oder Kleinschreibung zwar farblich markiert ist, jedoch nicht in die Wertung einfließt, um die Tippfähigkeiten der Schülerinnen und Schüler nicht zu stark zu gewichten.'
							}
						</ol>
					)} */}

					{subtest.id === 1 && (
						<>
							<div className="sub-test__legend print-hide">
								<span className="sub-test__legend__item sub-test__answer--correct">
									korrekt
								</span>
								<span
									className="sub-test__legend__item sub-test__answer--case"
									title="Nur die Groß-/Kleinschreibung weicht ab; wird nicht gewertet."
								>
									Groß-/Kleinschreibung inkorrekt
								</span>
								<span className="sub-test__legend__item sub-test__answer--wrong">
									inkorrekt
								</span>
							</div>
							<p className="sub-test__note" role="note">
								<strong>Hinweis:</strong> Unterschiede in der
								Groß-/Kleinschreibung werden farblich markiert,
								fließen aber <strong>nicht</strong> in die
								Bewertung ein, damit Tippfehler das Ergebnis
								nicht übermäßig beeinflussen.
							</p>
						</>
					)}
				</div>
			)
		);
	}
}

export default SubtestAnswers;
